import { useEffect, useState } from 'react';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, CalendarDays, Filter, RotateCcw } from 'lucide-react';
import { CompanySelect } from './CompanySelect';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

function getResponseData(json) {
  return Array.isArray(json) ? json[0] ?? null : json;
}

async function fetchFinancialData(url, token, signal) {
  const response = await fetch(url, {
    method: 'GET',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 204) return null;

  if (!response.ok) {
    const error = new Error(`Falha na consulta financeira (${response.status}).`);
    error.status = response.status;
    throw error;
  }

  return getResponseData(await response.json());
}

async function fetchOutstandingRecords(url, token, signal) {
  const response = await fetch(url, {
    method: 'GET',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 204) return [];

  if (!response.ok) {
    const error = new Error(`Falha na consulta financeira (${response.status}).`);
    error.status = response.status;
    throw error;
  }

  const json = await response.json();
  return Array.isArray(json) ? json : [];
}

function getOutstandingUrl(apiBaseUrl, path, filters) {
  const params = new URLSearchParams();

  if (filters.dataInicial) params.set('dataInicial', filters.dataInicial);
  if (filters.dataFinal) params.set('dataFinal', filters.dataFinal);
  if (filters.empresa !== 'TODAS') params.set('empresa', filters.empresa);

  const query = params.toString();
  return query ? `${apiBaseUrl}${path}?${query}` : `${apiBaseUrl}${path}`;
}

function createDefaultOutstandingFilters() {
  const today = new Date();
  const dataInicial = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const dataFinal = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;

  return { dataInicial, dataFinal, empresa: 'TODAS' };
}

function parseDueDate(value) {
  if (!value) return null;

  if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [day, month, year] = value.split('/').map(Number);
    const date = new Date(year, month - 1, day);

    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
      ? date
      : null;
  }

  const normalizedValue = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? `${value}T00:00:00`
    : value;
  const date = new Date(normalizedValue);

  return Number.isNaN(date.getTime()) ? null : date;
}

function getRecordValue(record, fieldNames) {
  const fieldName = fieldNames.find((name) => Object.hasOwn(record, name));
  return fieldName ? record[fieldName] : undefined;
}

function getOutstandingSummary(records, dueDateFields, amountFields, statusFields) {
  const monthlyTotals = new Map();
  const totals = { beforeToday: 0, currentMonthPaid: 0, future: 0 };

  records.forEach(record => {
    const dueDate = parseDueDate(getRecordValue(record, dueDateFields));
    const amount = Number(getRecordValue(record, amountFields));
    const status = String(getRecordValue(record, statusFields) || '').trim().toLocaleLowerCase('pt-BR');
    if (!Number.isFinite(amount)) return;

    let category;
    if (status === 'quitado' || status === 'quitados') {
      totals.currentMonthPaid += amount;
      category = 'paid';
    } else if (status === 'aberto' || status === 'renegociado' ) {
      totals.future += amount;
      category = 'future';
    } else if (status === 'vencido') {
      totals.beforeToday += amount;
      category = 'overdue';
    } else return;

    if (!dueDate) return;
    const year = dueDate.getFullYear();
    const month = dueDate.getMonth();
    const monthKey = year + '-' + String(month + 1).padStart(2, '0');
    const current = monthlyTotals.get(monthKey) || {
      key: monthKey, date: new Date(year, month, 1), paid: 0, future: 0, overdue: 0, open: 0,
    };
    current[category] += amount;
    // Aberto inclui os títulos a vencer e vencidos, sem incluir os quitados.
    current.open = current.future + current.overdue;
    monthlyTotals.set(monthKey, current);
  });

  return {
    ...totals,
    monthlyTotals: [...monthlyTotals.values()].sort((first, second) => first.date - second.date),
  };
}

function Metric({ label, value }) {
  return (
    <div>
      <dt className="text-[9px] font-bold uppercase tracking-[0.16em] text-gray-500">
        {label}
      </dt>
      <dd className="mt-1 font-mono text-xl font-bold tracking-tight text-white sm:text-2xl">
        {currencyFormatter.format(Number(value) || 0)}
      </dd>
    </div>
  );
}

function BalanceProgress({ balance: suppliedBalance, total, completed, totalLabel, completedLabel, balanceLabel, toneClass }) {
  const totalAmount = Number(total) || 0;
  const completedAmount = Number(completed) || 0;
  const balance = suppliedBalance == null ? totalAmount - completedAmount : Number(suppliedBalance) || 0;
  const progress = totalAmount > 0 ? Math.min(Math.max((completedAmount / totalAmount) * 100, 0), 100) : 0;

  return (
    <div className="mt-8 border-t border-gray-800 pt-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-[9px] font-bold uppercase tracking-[0.15em]">
        <span className="text-gray-500">{completedLabel} / {totalLabel}</span>
        <span className={toneClass}>{progress.toFixed(0)}% concluído</span>
      </div>

      <div
        className="mt-3 h-2 overflow-hidden border border-gray-800 bg-gray-900"
        aria-label={`${completedLabel}: ${progress.toFixed(0)}% de ${totalLabel}`}
        role="progressbar"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={Math.round(progress)}
      >
        <div
          className={`h-full transition-all duration-700 ${toneClass === 'text-green-400' ? 'bg-green-500' : 'bg-amber-400'}`}
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="mt-4 flex items-end justify-between gap-4">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-gray-500">{balanceLabel}</p>
          <p className="mt-1 font-mono text-lg font-bold text-white">{currencyFormatter.format(balance)}</p>
        </div>
      </div>
    </div>
  );
}

function CashFlowCard({
  title,
  icon: Icon,
  accentClass,
  metrics,
  loading,
  error,
  isEmpty,
  progress,
}) {
  return (
    <section className={`min-h-10 border-l-2 ${accentClass} bg-[#161b22] p-5 shadow-xl`} aria-live="polite">
      {/*<header className="flex items-center gap-3 border-b border-gray-800 pb-4">
        <Icon aria-hidden="true" size={18} className="text-gray-300" />
        <div>
          <p className="text-[9px] font-bold uppercase tracking-[0.24em] text-gray-500">Fluxo de caixa</p>
          <h2 className="mt-1 text-sm font-black uppercase tracking-wide text-white">{title}</h2>
        </div>
      </header>*/}

      {loading && (
        <div className="mt-6 space-y-4 animate-pulse" aria-label={`Carregando ${title.toLowerCase()}`}>
          <div className="h-3 w-2/5 bg-gray-800" />
          <div className="h-8 w-4/5 bg-gray-800" />
          <div className="h-3 w-1/3 bg-gray-800" />
          <div className="h-8 w-3/5 bg-gray-800" />
        </div>
      )}

      {!loading && error && (
        <div className="mt-6 flex gap-3 text-sm leading-6 text-amber-300" role="alert">
          <AlertTriangle aria-hidden="true" size={19} className="mt-1 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && isEmpty && (
        <p className="mt-6 text-sm leading-6 text-gray-400">
          Não há dados disponíveis para esta referência.
        </p>
      )}

      {!loading && !error && !isEmpty && (
        <>
          <dl className="mt-6 grid gap-6 sm:grid-cols-2">
            {metrics.map((metric) => <Metric key={metric.label} {...metric} />)}
          </dl>
          <BalanceProgress {...progress} />
        </>
      )}
    </section>
  );
}

function OutstandingTotal({ contextLabel, label, value, accentClass }) {
  return (
    <div role="group" aria-label={`${contextLabel}: ${label}`} className={`border-l-2 ${accentClass} bg-[#161b22] p-4`}>
      <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-gray-500">{label}</p>
      <p className="mt-2 font-mono text-xl font-bold tracking-tight text-white">
        {currencyFormatter.format(value)}
      </p>
    </div>
  );
}

const monthlySeries = [
  { key: 'paid', label: 'Quitado', color: 'bg-green-500' },
  { key: 'future', label: 'A vencer', color: 'bg-blue-400' },
  { key: 'open', label: 'Total em aberto', color: 'bg-amber-400', hidden: true },
  { key: 'overdue', label: 'Vencido', color: 'bg-red-500' },
].filter(series => !series.hidden);
const monthlyLabel = date => new Intl.DateTimeFormat('pt-BR', { month: 'short', year: '2-digit' })
  .formatToParts(date).filter(part => part.type === 'month' || part.type === 'year')
  .map(part => part.value.replace('.', '')).join('/');
const compactCurrency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact', maximumFractionDigits: 1 });

function MonthlyFinancialChart({ totals, contextLabel }) {
  if (totals.length === 0) {
    return <p className="py-8 text-sm text-gray-400">Não há títulos com vencimento válido para exibir no gráfico.</p>;
  }
  const chartTotals = totals.filter(month => monthlySeries.some(series => month[series.key] !== 0));
  if (chartTotals.length === 0) return <p className="py-8 text-sm text-gray-400">Não há valores diferentes de zero para exibir no gráfico.</p>;
  const greatestValue = Math.max(...chartTotals.flatMap(month => monthlySeries.map(series => Math.abs(month[series.key]))), 1);

  return (
    <div className="mt-5 min-w-0">
      <ul className="flex flex-wrap gap-x-5 gap-y-2 text-[10px] text-gray-300" aria-label={'Legenda de ' + contextLabel.toLowerCase()}>
        {monthlySeries.map(series => <li key={series.key} className="flex items-center gap-2"><span aria-hidden="true" className={'h-2.5 w-2.5 rounded-sm ' + series.color} />{series.label}</li>)}
      </ul>
      <p className="mt-3 text-[10px] leading-5 text-gray-400">Por mês de vencimento.</p>
      <div className="mt-5 overflow-x-auto pb-3" tabIndex={0} role="region" aria-label={'Gráfico mensal de ' + contextLabel.toLowerCase()}>
        <ol className="flex min-w-max items-end gap-6" aria-label="Títulos por mês de vencimento">
          {chartTotals.map(month => {
            const label = monthlyLabel(month.date);
            return (
              <li key={month.key} role="group" aria-label={contextLabel + ': ' + label} className="w-64 shrink-0">
                <div className="grid grid-cols-3 gap-2">
                  {monthlySeries.map(series => {
                    const value = month[series.key];
                    if (value === 0) return <div key={series.key} aria-hidden="true" />;
                    const height = value === 0 ? 0 : Math.max(Math.abs(value) / greatestValue * 100, 2);
                    return <div key={series.key} className="min-w-0" title={series.label + ' — ' + label + ': ' + currencyFormatter.format(value)}>
                      <span className="mb-2 block text-center font-mono text-[9px] text-gray-300">{compactCurrency.format(value)}</span>
                      <div className="flex h-36 items-end border-b border-gray-700" role="img" aria-label={series.label + ': ' + currencyFormatter.format(value)}>
                        <div className={'w-full rounded-t-sm ' + series.color} style={{ height: height + '%' }} />
                      </div>
                    </div>;
                  })}
                </div>
                <p className="mt-3 text-center text-[10px] font-bold uppercase tracking-wide text-gray-300">{label}</p>
              </li>
            );
          })}
        </ol>
      </div>
      <details className="mt-3 border-t border-gray-800 pt-3">
        <summary className="cursor-pointer py-2 text-xs text-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-400">Consultar valores por mês</summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-right text-xs tabular-nums">
            <caption className="sr-only">{contextLabel}: valores por mês de vencimento</caption>
            <thead><tr><th scope="col" className="px-2 py-2 text-left text-gray-400">Mês</th>{monthlySeries.map(series => <th scope="col" key={series.key} className="whitespace-nowrap px-2 py-2 font-medium text-gray-400">{series.label}</th>)}</tr></thead>
            <tbody>{totals.map(month => <tr key={month.key} className="border-t border-gray-800"><th scope="row" className="whitespace-nowrap px-2 py-3 text-left font-medium text-gray-300">{monthlyLabel(month.date)}</th>{monthlySeries.map(series => <td key={series.key} className="whitespace-nowrap px-2 py-3 font-mono text-white">{currencyFormatter.format(month[series.key])}</td>)}</tr>)}</tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function OutstandingOverview({ title, icon: Icon, records, loading, error, dueDateFields, amountFields, statusFields }) {
  const summary = getOutstandingSummary(records, dueDateFields, amountFields, statusFields);

  return (
    <section className="min-w-0 border border-gray-800 bg-[#10161d] p-5 shadow-xl sm:p-6" aria-live="polite">
      <header className="flex items-center gap-3 border-b border-gray-800 pb-4">
        <Icon aria-hidden="true" size={18} className="text-gray-300" />
        <div>
          <p className="text-[9px] font-bold uppercase tracking-[0.24em] text-gray-500">Títulos financeiros</p>
          <h2 className="mt-1 text-sm font-black uppercase tracking-wide text-white">{title}</h2>
        </div>
      </header>

      {loading && (
        <div className="mt-5 grid gap-3 md:grid-cols-3 animate-pulse" aria-label={`Carregando ${title.toLowerCase()}`}>
          {[1, 2, 3].map((item) => <div key={item} className="h-20 bg-gray-800" />)}
        </div>
      )}

      {!loading && error && (
        <div className="mt-5 flex gap-3 text-sm leading-6 text-amber-300" role="alert">
          <AlertTriangle aria-hidden="true" size={19} className="mt-1 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <OutstandingTotal
              contextLabel={title}
              label="Total vencido antes do dia atual"
              value={summary.beforeToday}
              accentClass="border-red-500"
            />
            <OutstandingTotal
              contextLabel={title}
              label="Total quitado no mês atual"
              value={summary.currentMonthPaid}
              accentClass="border-amber-400"
            />
            <OutstandingTotal
              contextLabel={title}
              label="Total a vencer"
              value={summary.future}
              accentClass="border-blue-400"
            />
          </div>

          <div className="mt-6 border-t border-gray-800 pt-5">
            <div className="flex items-center gap-2">
              <CalendarDays aria-hidden="true" size={16} className="text-red-400" />
              <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-300">
                Quitado, a vencer e vencido por mês 
              </h3>
            </div>
            <MonthlyFinancialChart totals={summary.monthlyTotals} contextLabel={title} />
          </div>
        </>
      )}
    </section>
  );
}

function OutstandingFilters({ apiBaseUrl, filters, onChange, onApply, onClear, error }) {
  return (
    <form
      className="mb-6 border border-gray-800 bg-[#10161d] p-4 shadow-xl"
      onSubmit={(event) => {
        event.preventDefault();
        onApply();
      }}
    >
      <div className="mb-4 flex items-center gap-2 border-b border-gray-800 pb-3">
        <Filter aria-hidden="true" size={16} className="text-green-400" />
        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-300">
          Filtros dos títulos financeiros
        </h2>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-gray-500">Data inicial</span>
          <input
            type="date"
            value={filters.dataInicial}
            onChange={(event) => onChange({ ...filters, dataInicial: event.target.value })}
            className="h-10 border border-gray-700 bg-gray-900 px-3 text-sm text-white outline-none focus:border-green-400"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-gray-500">Data final</span>
          <input
            type="date"
            value={filters.dataFinal}
            onChange={(event) => onChange({ ...filters, dataFinal: event.target.value })}
            className="h-10 border border-gray-700 bg-gray-900 px-3 text-sm text-white outline-none focus:border-green-400"
          />
        </label>
        <div>
          <CompanySelect apiBaseUrl={apiBaseUrl} value={filters.empresa} onChange={empresa => onChange({ ...filters, empresa })} allValue="TODAS" />
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-amber-300" role="alert">{error}</p>}

      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="submit"
          className="inline-flex min-h-10 items-center gap-2 bg-green-500 px-4 text-[10px] font-bold uppercase tracking-[0.14em] text-[#08110b] transition-colors hover:bg-green-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-400"
        >
          <Filter aria-hidden="true" size={15} /> Aplicar filtros
        </button>
        <button
          type="button"
          onClick={onClear}
          className="inline-flex min-h-10 items-center gap-2 border border-gray-700 px-4 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-300 transition-colors hover:border-gray-500 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-400"
        >
          <RotateCcw aria-hidden="true" size={15} /> Limpar
        </button>
      </div>
    </form>
  );
}

export default function FinancialDashboard({ apiBaseUrl, onSessionExpired, canViewReceipts = true, canViewPayments = true }) {
  const [receipts, setReceipts] = useState(null);
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({ receipts: null, payments: null });
  const [outstandingRecords, setOutstandingRecords] = useState({ receipts: [], payments: [] });
  const [outstandingLoading, setOutstandingLoading] = useState(true);
  const [outstandingErrors, setOutstandingErrors] = useState({ receipts: null, payments: null });
  const [draftFilters, setDraftFilters] = useState(createDefaultOutstandingFilters);
  const [appliedFilters, setAppliedFilters] = useState(createDefaultOutstandingFilters);
  const [filterError, setFilterError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('@user_token');
    const controller = new AbortController();

    if (!token) {
      setLoading(false);
      setErrors({ receipts: 'Sua sessão expirou. Entre novamente para consultar os dados.', payments: null });
      return undefined;
    }

    async function loadCashFlow() {
      setLoading(true);
      setErrors({ receipts: null, payments: null });

      const [receiptsResult, paymentsResult] = await Promise.allSettled([
        canViewReceipts ? fetchFinancialData(`${apiBaseUrl}/api/dashboard/financeiro`, token, controller.signal) : Promise.resolve(null),
        canViewPayments ? fetchFinancialData(`${apiBaseUrl}/api/dashboard/financeiro/ContaPagar`, token, controller.signal) : Promise.resolve(null),
      ]);

      if (controller.signal.aborted) return;

      const hasExpiredSession = [receiptsResult, paymentsResult].some(
        (result) => result.status === 'rejected' && result.reason?.status === 401,
      );

      if (hasExpiredSession) {
        onSessionExpired();
        return;
      }

      if (receiptsResult.status === 'fulfilled') {
        setReceipts(receiptsResult.value);
      }

      if (paymentsResult.status === 'fulfilled') {
        setPayments(paymentsResult.value);
      }

      setErrors({
        receipts: receiptsResult.status === 'rejected'
          ? 'Não foi possível carregar os recebimentos. Tente novamente mais tarde.'
          : null,
        payments: paymentsResult.status === 'rejected'
          ? 'Não foi possível carregar os pagamentos. Tente novamente mais tarde.'
          : null,
      });
      setLoading(false);
    }

    loadCashFlow();

    return () => controller.abort();
  }, [apiBaseUrl, onSessionExpired, canViewReceipts, canViewPayments]);

  useEffect(() => {
    const token = localStorage.getItem('@user_token');
    const controller = new AbortController();

    if (!token) {
      setOutstandingLoading(false);
      setOutstandingErrors({
        receipts: 'Sua sessão expirou. Entre novamente para consultar os dados.',
        payments: 'Sua sessão expirou. Entre novamente para consultar os dados.',
      });
      return undefined;
    }

    async function loadOutstandingRecords() {
      setOutstandingLoading(true);
      setOutstandingErrors({ receipts: null, payments: null });

      const [receiptsResult, paymentsResult] = await Promise.allSettled([
        canViewReceipts ? fetchOutstandingRecords(getOutstandingUrl(apiBaseUrl, '/api/dashboard/financeiro/recebimentos/detalhe', appliedFilters), token, controller.signal) : Promise.resolve([]),
        canViewPayments ? fetchOutstandingRecords(getOutstandingUrl(apiBaseUrl, '/api/dashboard/financeiro/contas-pagar/detalhe', appliedFilters), token, controller.signal) : Promise.resolve([]),
      ]);

      if (controller.signal.aborted) return;

      const hasExpiredSession = [receiptsResult, paymentsResult].some(
        (result) => result.status === 'rejected' && result.reason?.status === 401,
      );

      if (hasExpiredSession) {
        onSessionExpired();
        return;
      }

      setOutstandingRecords({
        receipts: receiptsResult.status === 'fulfilled' ? receiptsResult.value : [],
        payments: paymentsResult.status === 'fulfilled' ? paymentsResult.value : [],
      });
      setOutstandingErrors({
        receipts: receiptsResult.status === 'rejected'
          ? 'Não foi possível carregar os recebimentos. Tente novamente mais tarde.'
          : null,
        payments: paymentsResult.status === 'rejected'
          ? 'Não foi possível carregar os pagamentos. Tente novamente mais tarde.'
          : null,
      });
      setOutstandingLoading(false);
    }

    loadOutstandingRecords();

    return () => controller.abort();
  }, [apiBaseUrl, appliedFilters, onSessionExpired, canViewReceipts, canViewPayments]);

  function applyFilters() {
    if (draftFilters.dataInicial && draftFilters.dataFinal && draftFilters.dataInicial > draftFilters.dataFinal) {
      setFilterError('A data inicial não pode ser posterior à data final.');
      return;
    }

    setFilterError(null);
    setAppliedFilters(draftFilters);
  }

  function clearFilters() {
    const defaultFilters = createDefaultOutstandingFilters();
    setFilterError(null);
    setDraftFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  }

  return (
    <section className="min-w-0" aria-labelledby="financial-title">   
          
      <div className="grid gap-1 xl:grid-cols-2">
      
        {canViewReceipts && <CashFlowCard
          title="Recebimentos"
          icon={ArrowDownLeft}
          accentClass="border-green-500"
          loading={loading}
          error={errors.receipts}
          isEmpty={!receipts}
          metrics={[             
            { label: 'A receber no mês', value: receipts?.recebidoMes }, 
            { label: 'Recebido no mês', value: receipts?.valorRecebidoMes }, 
            
          ]}
           
          
          progress={{
            total: receipts?.recebidoMes,
            completed: receipts?.recebidoMes - receipts?.aReceberMes,
            totalLabel: 'a receber no mês',
            completedLabel: 'recebido no mês',
            balanceLabel: 'saldo a receber',
            toneClass: 'text-green-400',
          }}
        />}
        {canViewPayments && <CashFlowCard
          title="Pagamentos"
          icon={ArrowUpRight}
          accentClass="border-amber-400"
          loading={loading}
          error={errors.payments}
          isEmpty={!payments}
          metrics={[
            { label: 'A pagar no mês', value: payments?.aPagarMes },
            { label: 'Quitados no mês', value: payments?.quitadosMes },
            { label: 'Acréscimos', value: payments?.acrescimo },
            { label: 'Descontos', value: payments?.desconto },
          ]}
          progress={{
            total: payments?.aPagarMes,
            completed: payments?.quitadosMes,
            balance: payments?.saldoEmAberto ?? 0,
            totalLabel: 'total do mês',
            completedLabel: 'quitados no mês',
            balanceLabel: 'saldo a pagar',
            toneClass: 'text-amber-300',
          }}
        />}
      </div>

      <div className="mt-6 grid gap-6">
        <OutstandingFilters
          apiBaseUrl={apiBaseUrl}
          filters={draftFilters}
          onChange={setDraftFilters}
          onApply={applyFilters}
          onClear={clearFilters}
          error={filterError}
        />
        {canViewReceipts && <OutstandingOverview
          title="Recebimentos"
          icon={ArrowDownLeft}
          records={outstandingRecords.receipts}
          loading={outstandingLoading}
          error={outstandingErrors.receipts}
          dueDateFields={['Data Vencimento', 'data_vencimento', 'DataVencimento', 'VENCIMENTO']}
          amountFields={['valor', 'ValorPago', 'Valor', 'VALOR']}
          statusFields={['Status', 'status', 'STATUS']}
        />}
        {canViewPayments && <OutstandingOverview
          title="Pagamentos"
          icon={ArrowUpRight}
          records={outstandingRecords.payments}
          loading={outstandingLoading}
          error={outstandingErrors.payments}
          dueDateFields={['DataVencimento', 'data_vencimento', 'Data Vencimento', 'VENCIMENTO']}
          amountFields={['valor_status', 'ValorStatus', 'ValorPago', 'valor', 'Valor', 'VALOR']}
          statusFields={['Status', 'status', 'STATUS']}
        />}
      </div> 
    </section>
  );
}
